import React, { useState, useMemo, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import {
    CircularProgress,
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    styled,
    Checkbox
} from "@mui/material";
import CustomTooltip from "../../../../utils/CustomTooltip";
import SnackBarMessage from "../../../components/snackbar/snackbar";
import AlertPagination from "./AlertPagination";
import { flattenMaterialGroups, buildMaterialDisplayRows } from "./materialGrouping";
import styles from "./alertTables.module.css";
import vector from "../../../../assets/images/Vector.svg";
import ExpandRight from "../../../../assets/images/expandRight.svg";
import { SelectedAlertsContext } from "../../../../utils/SelectedAlertContext";
import { formatText } from "utils/formatText";

// Constants
const ROWS_PER_PAGE_OPTIONS = [15, 25, 50, 100];
const INITIAL_ROWS_PER_PAGE = 25;
const INITIAL_PAGE = 0;
const CRITICAL_COLORS = {
    HIGH: "var(--high-critical)",
    MEDIUM: "var(--medium-critical)",
    LOW: "var(--low-critical)",
    NOCOLOR: "var(--no-color-critical)"
};
const DAYS_TO_ACTION_RANGES = {
    HIGH: 7,
    MEDIUM: 15
};
const TEXT_HEAVY_COLUMNS = new Set(["Material description", "Vendor name", "Root cause", "Buyer"]);
const TRUNCATABLE_COLUMNS = new Set(["Material description", "Vendor name", "Root cause"]);
const ID_COLUMNS = new Set(["Alert ID", "CBU code", "Site code"]);
const PERCENTAGE_COLUMNS = new Set(["Actual SOB%", "SOB Compliance%"]);

// Styled Components
const StyledTableCell = styled(TableCell)(({ theme }) => ({
    "&.MuiTableCell-head": {
        fontFamily: "Unilever Shilling",
        backgroundColor: "var(--primany-table-header-color)",
        color: "#fff",
        textAlign: "center",
        padding: "5px",
        fontSize: "12px",
        fontWeight: 600,
        outline: "1px solid #EAECF0",
        whiteSpace: "nowrap"
    },
    "&.MuiTableCell-body": {
        fontFamily: "Unilever Shilling",
        fontSize: "12px",
        fontWeight: 400,
        padding: "5px",
        color: "#2B2B2B",
        border: "1px solid #EAECF0",
        textAlign: "center",
        whiteSpace: "nowrap",
    },
}));

const StyledTableRow = styled(TableRow)({
    backgroundColor: "#FFFFFF !important"
});

const ContractAlertTable = ({ data, columnList, type, paginationObj, onPageChange, loading, isReadOnly, groupByMaterialId = false }) => {
    const normalizedData = useMemo(
        () => groupByMaterialId ? flattenMaterialGroups(data) : data,
        [data, groupByMaterialId],
    );
    const [localItems, setLocalItems] = useState(normalizedData);
    const { selections, setSelections } = useContext(SelectedAlertsContext);
    const [sortConfig, setSortConfig] = useState({ key: "", direction: "asc" });
    const [page, setPage] = useState(INITIAL_PAGE);
    const [rowsPerPage, setRowsPerPage] = useState(INITIAL_ROWS_PER_PAGE);
    const [snackBarObject, setSnackBarObject] = useState({
        snackbarOpen: false,
        message: '',
        type: 'message'
    });

    const [expandedColumns, setExpandedColumns] = useState({});

    // Filter columns based on visibility
    const visibleColumns = useMemo(() => {
        return columnList.filter(col => col.visibility !== false);
    }, [columnList]);

    // Effects
    useEffect(() => {
        setLocalItems(normalizedData);
        if (!paginationObj && !onPageChange) {
            setPage(INITIAL_PAGE);
        }
    }, [normalizedData]);

    const sortedData = useMemo(() => {
        if (!sortConfig.key) return localItems;

        return [...localItems].sort((a, b) => {
            const valueA = a[sortConfig.key];
            const valueB = b[sortConfig.key];

            if (!Number.isNaN(valueA) && !Number.isNaN(valueB)) {
                return sortConfig.direction === "asc" ?
                    valueA - valueB : valueB - valueA;
            }

            if (typeof valueA === "string" && typeof valueB === "string") {
                return sortConfig.direction === "asc" ?
                    valueA.localeCompare(valueB) :
                    valueB.localeCompare(valueA);
            }

            return 0;
        });
    }, [localItems, sortConfig]);

    const displayedData = useMemo(() => {
        const currentRowsPerPage = paginationObj?.per_page || rowsPerPage;

        if (paginationObj || onPageChange) {
            return groupByMaterialId ? sortedData : sortedData.slice(0, currentRowsPerPage);
        }
        return sortedData.slice(page * currentRowsPerPage, page * currentRowsPerPage + currentRowsPerPage);
    }, [sortedData, paginationObj, onPageChange, page, rowsPerPage, groupByMaterialId]);

    // Helper functions
    const getCriticalColor = (alert) => {
        if (type !== "Contract PR2 Alert") {
            return CRITICAL_COLORS.MEDIUM;
        }

        const days = alert["Days to action"];

        if (days === "N/A" || Number.isNaN(Number(days)) || Number(days) > 21) {
            return "transparent";
        }

        const numericDays = Number(days);

        if (numericDays <= DAYS_TO_ACTION_RANGES.HIGH) return CRITICAL_COLORS.HIGH;
        if (numericDays <= DAYS_TO_ACTION_RANGES.MEDIUM) return CRITICAL_COLORS.MEDIUM;
        return CRITICAL_COLORS.LOW;
    };


    const handleCheckboxChange = (alert) => {
        setSelections(prev => {
            const existsInAccepted = prev.contract?.accepted?.some(item => item["Alert ID"] === alert["Alert ID"]);
            const existsInRejected = prev.contract?.rejected?.some(item => item["Alert ID"] === alert["Alert ID"]);

            let updatedRejected = prev.contract?.rejected || [];
            if (existsInRejected) {
                updatedRejected = updatedRejected.filter(item => item["Alert ID"] !== alert["Alert ID"]);
            }

            if (existsInAccepted) {
                return {
                    ...prev,
                    contract: {
                        ...prev.contract,
                        accepted: prev.contract.accepted.filter(item => item["Alert ID"] !== alert["Alert ID"]),
                        rejected: updatedRejected
                    }
                };
            } else {
                setSnackBarObject({
                    snackbarOpen: true,
                    message: 'Alert selected.',
                    type: 'success'
                });

                return {
                    ...prev,
                    contract: {
                        ...prev.contract,
                        accepted: [
                            ...(prev.contract?.accepted || []),
                            alert
                        ],
                        rejected: updatedRejected
                    }
                };
            }
        });
    };

    const handleCheckboxRejectedChange = (alert) => {
        setSelections(prev => {
            const existsInAccepted = prev.contract?.accepted?.some(item => item["Alert ID"] === alert["Alert ID"]);
            const existsInRejected = prev.contract?.rejected?.some(item => item["Alert ID"] === alert["Alert ID"]);

            let updatedAccepted = prev.contract?.accepted || [];
            if (existsInAccepted) {
                updatedAccepted = updatedAccepted.filter(item => item["Alert ID"] !== alert["Alert ID"]);
            }

            if (existsInRejected) {
                return {
                    ...prev,
                    contract: {
                        ...prev.contract,
                        accepted: updatedAccepted,
                        rejected: prev.contract.rejected.filter(item => item["Alert ID"] !== alert["Alert ID"])
                    }
                };
            } else {
                return {
                    ...prev,
                    contract: {
                        ...prev.contract,
                        accepted: updatedAccepted,
                        rejected: [...(prev.contract?.rejected || []), alert]
                    }
                };
            }
        });
    };

    // Toggle column expansion
    const toggleColumnExpansion = (columnLabel, e) => {
        e.stopPropagation(); // Prevent triggering sort
        setExpandedColumns(prev => ({
            ...prev,
            [columnLabel]: !prev[columnLabel]
        }));
    };

    // Check if column can be expanded/collapsed for width
    const isTruncatableColumn = (label) => {
        return TRUNCATABLE_COLUMNS.has(label);
    };

    // Event handlers
    const handleSort = (key) => {
        setSortConfig(prev => ({
            key,
            direction: prev.key === key && prev.direction === "asc" ? "desc" : "asc",
        }));
    };

    const handlePageChange = (event, newPage, pageSize = rowsPerPage) => {
        if (!paginationObj && !onPageChange) {
            setPage(newPage);
        } else {
            const actualPage = newPage + 1;
            if (onPageChange) {
                onPageChange(actualPage, pageSize);
            }
        }
    };

    const handleRowsPerPageChange = (newRowsPerPage) => {
        setRowsPerPage(newRowsPerPage);

        if (!paginationObj && !onPageChange) {
            setPage(INITIAL_PAGE);
        } else if (onPageChange) {
            onPageChange(1, newRowsPerPage);
        }
    };

    const closeSnackbar = () => {
        setSnackBarObject({ message: '', snackbarOpen: false, type: 'error' });
    };

    const renderTextHeavyCell = (col, alert) => {
        const isExpanded = expandedColumns[col.label];
        return (
            <StyledTableCell
                key={`${alert["Alert ID"] || 'row'}-${col.label}`}
                className={!isExpanded ? styles.ellipsisText : ''}
                sx={{
                    minWidth: isExpanded ? '300px' : undefined,
                    maxWidth: isExpanded ? '300px' : undefined,
                    width: isExpanded ? '300px' : undefined,
                    whiteSpace: isExpanded ? 'normal' : 'nowrap',
                    overflow: isExpanded ? 'visible' : 'hidden',
                    textOverflow: isExpanded ? 'clip' : 'ellipsis',
                }}
            >
                <CustomTooltip title={formatText(alert[col.label])}>
                    <span>{formatText(alert[col.label])}</span>
                </CustomTooltip>
            </StyledTableCell>
        );
    };

    const renderCriticalityCell = (col, alert) => {
        return (
            <StyledTableCell key={`${alert["Alert ID"] || 'row'}-${col.label}`} style={{ display: "flex", justifyContent: "center" }}>
                <span
                    style={{
                        backgroundColor: getCriticalColor(alert),
                        borderRadius: "8px",
                        padding: "1px",
                        minWidth: "40px",
                        textAlign: "center"
                    }}
                >
                    {alert[col.label]}
                </span>
            </StyledTableCell>
        );
    };

    const getDefaultCellValue = (col, alert) => {
        if (ID_COLUMNS.has(col.label)) {
            return alert[col.label];
        }

        if (PERCENTAGE_COLUMNS.has(col.label)) {
            return alert[col.label]?.toFixed(2);
        }

        if (col.label !== 'Action Owner') {
            return formatText(alert[col.label]);
        }

        return alert[col.label];
    };

    // Render helpers
    const renderTableCell = (col, alert) => {
        if (TEXT_HEAVY_COLUMNS.has(col.label)) {
            return renderTextHeavyCell(col, alert);
        }

        if ((col.label === "Ageing" && alert[col.label] >= 3) || col.label === 'Days to action') {
            return renderCriticalityCell(col, alert);
        }

        return (
            <StyledTableCell key={`${alert["Alert ID"] || 'row'}-${col.label}`} style={{ textAlign: col.label === 'PR Qty' ? 'right' : 'center' }}>
                {getDefaultCellValue(col, alert)}
            </StyledTableCell>
        );
    };

    const renderActionButtons = (alert) => {
        if (type === "Contract PR2 Alert") return null;

        const isDisabled = ["Rejected", "Closed", "Accepted"].includes(alert["Alert status"]);

        // For Closed alerts, check if it was previously accepted  
        const isAcceptedChecked = alert["Alert status"] === "Accepted" ||
            (alert["Alert status"] === "Closed" &&
                selections.contract?.accepted?.some(item => item["Alert ID"] === alert["Alert ID"])) ||
            selections.contract?.accepted?.some(item => item["Alert ID"] === alert["Alert ID"]);

        // For Closed alerts, check if it was previously rejected  
        const isRejectedChecked = alert["Alert status"] === "Rejected" ||
            (alert["Alert status"] === "Closed" &&
                selections.contract?.rejected?.some(item => item["Alert ID"] === alert["Alert ID"])) ||
            selections.contract?.rejected?.some(item => item["Alert ID"] === alert["Alert ID"]);

        return (
            <>
                <StyledTableCell>
                    <Checkbox
                        checked={!!isAcceptedChecked}
                        onChange={() => handleCheckboxChange(alert)}
                        disabled={isReadOnly || isDisabled}
                        color="primary"
                        sx={{
                            padding: 0,
                            color: isDisabled ? "rgb(0 0 0 / 15%)" : "#B0B0B0",
                            "& .MuiSvgIcon-root": {
                                fontSize: "16px",
                            }
                        }}
                    />
                </StyledTableCell>
                <StyledTableCell>
                    <Checkbox
                        checked={!!isRejectedChecked}
                        onChange={() => handleCheckboxRejectedChange(alert)}
                        disabled={isReadOnly ||isDisabled}
                        color="primary"
                        sx={{
                            padding: 0,
                            color: isDisabled ? "rgb(0 0 0 / 15%)" : "#B0B0B0",
                            "& .MuiSvgIcon-root": {
                                fontSize: "16px",
                            }
                        }}
                    />
                </StyledTableCell>
            </>
        );
    };

    const renderLegends = () => {
        if (type !== 'Contract PR2 Alert') return null;

        return (
            <div className={styles.contractLegends}>
                {[
                    { color: CRITICAL_COLORS.HIGH, label: "0 to 7" },
                    { color: CRITICAL_COLORS.MEDIUM, label: "8 to 15" },
                    { color: CRITICAL_COLORS.LOW, label: "16 to 21" },
                    { color: CRITICAL_COLORS.NOCOLOR }
                ].map((legend) => (
                    <div key={`legend-${legend.label || 'none'}-${legend.color}`} className={styles.contractLegendsDiv}>
                        <span
                            className={styles.legends}
                            style={{ backgroundColor: legend.color }}
                        />
                        <span>{legend.label}</span>
                    </div>
                ))}
            </div>
        );
    };

    const getRowKey = (alert, index) => {
        const fallbackKey = [alert["CBU code"], alert["Site code"], alert["Vendor name"]]
            .filter(Boolean)
            .join("-");

        return `${paginationObj?.page || page + 1}-${alert["Alert ID"] || fallbackKey || 'alert'}-${index}`;
    };

    return (
        <>
            <Box sx={{ position: "relative" }}>
                {loading && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            bottom: 0,
                            backgroundColor: 'rgba(255, 255, 255, 0.7)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 20,
                        }}
                    >
                        <CircularProgress />
                    </Box>
                )}
                <TableContainer className={`${styles.table} ${styles.pageScrollTable} ${styles.stockToNormTableHeight}`}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow>
                                {visibleColumns.map((col, index) => (
                                    <StyledTableCell
                                        key={col.label}
                                        onClick={() => col.sortable && handleSort(col.label)}
                                        sx={{
                                            cursor: col.sortable ? "pointer" : "default",
                                            minWidth: expandedColumns[col.label] ? '300px' : undefined,
                                            maxWidth: expandedColumns[col.label] ? '300px' : undefined,
                                            width: expandedColumns[col.label] ? '300px' : undefined,
                                        }}
                                        colSpan={col.label === 'Accept' ? 2 : 1}
                                    >
                                        <div className={styles.sortIcon} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 6 }}>
                                            <span className={!expandedColumns[col.label] ? styles.ellipsisTextColumnHeader : ''} style={{ maxWidth: "100px", flex: 1 }}>
                                                <CustomTooltip title={col.label}>{col.label}</CustomTooltip>
                                            </span>
                                            <div style={{ display: 'flex', gap: 2, flexShrink: 0 }}>
                                                {col.sortable && sortConfig.key === col.label && (
                                                    <img
                                                        src={vector}
                                                        alt="sort icon"
                                                        width="14px"
                                                        height="14px"
                                                        style={{
                                                            marginLeft: "4px",
                                                        }}
                                                    />
                                                )}
                                                {isTruncatableColumn(col.label) && (
                                                    <button
                                                        type="button"
                                                        onClick={(e) => toggleColumnExpansion(col.label, e)}
                                                        title="Expand/Collapse column"
                                                        style={{
                                                            display: "inline-flex",
                                                            alignItems: "center",
                                                            cursor: "pointer",
                                                            border: "none",
                                                            background: "transparent",
                                                            padding: 0
                                                        }}
                                                    >
                                                        <img src={ExpandRight} alt="expandRight" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </StyledTableCell>
                                ))}
                                {type === 'Contract Alert' && <StyledTableCell>Accept</StyledTableCell>}
                                {type === 'Contract Alert' && <StyledTableCell>Reject</StyledTableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {sortedData.length > 0 ? (
                                buildMaterialDisplayRows(displayedData, groupByMaterialId).map(({ row: alert, materialId, showHeader }, index) => (
                                    <React.Fragment key={getRowKey(alert, index)}>
                                        {showHeader && (
                                            <StyledTableRow data-testid="material-group-row">
                                                <StyledTableCell
                                                    colSpan={visibleColumns.length + (type === 'Contract Alert' ? 2 : 0)}
                                                    sx={{ textAlign: "left", fontWeight: 700, backgroundColor: "#F2F4F7" }}
                                                    style={{
                                                        textAlign: 'left',
                                                        color: 'black',
                                                        fontSize: '12px',
                                                        fontWeight: 700,
                                                        textTransform: 'uppercase',
                                                    }}
                                                >
                                                    {materialId}
                                                </StyledTableCell>
                                            </StyledTableRow>
                                        )}
                                    <StyledTableRow key={getRowKey(alert, index)}>
                                        {visibleColumns.map((col) => {
                                            return renderTableCell(col, alert);
                                        })}
                                        {type === 'Contract Alert' && renderActionButtons(alert)}
                                    </StyledTableRow>
                                    </React.Fragment>
                                ))
                            ) : (
                                <StyledTableRow key="Not available">
                                    <StyledTableCell colSpan={visibleColumns.length + 1}>
                                        {loading ? "Loading..." : "Data not available..."}
                                    </StyledTableCell>
                                </StyledTableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
            {renderLegends()}

            <AlertPagination
                paginationObj={paginationObj}
                onPageChange={onPageChange}
                localPage={page}
                rowsPerPage={rowsPerPage}
                totalRecords={sortedData.length}
                onChange={handlePageChange}
                onRowsPerPageChange={handleRowsPerPageChange}
                rowsPerPageOptions={ROWS_PER_PAGE_OPTIONS}
            />
            {snackBarObject["snackbarOpen"] && (
                <SnackBarMessage
                    type={snackBarObject["type"]}
                    snackbarMessage={snackBarObject["message"]}
                    snackbarOpen={snackBarObject["snackbarOpen"]}
                    setSnackbarOpen={closeSnackbar}
                />
            )}
        </>
    );
};

ContractAlertTable.propTypes = {
    data: PropTypes.arrayOf(PropTypes.object),
    columnList: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            visibility: PropTypes.bool,
            sortable: PropTypes.bool,
        })
    ),
    type: PropTypes.string,
    paginationObj: PropTypes.shape({
        page: PropTypes.number,
        per_page: PropTypes.number,
        total: PropTypes.number,
        total_pages: PropTypes.number,
        totalPages: PropTypes.number,
    }),
    onPageChange: PropTypes.func,
    loading: PropTypes.bool,
    groupByMaterialId: PropTypes.bool,
    isReadOnly: PropTypes.bool,
};

ContractAlertTable.defaultProps = {
    data: [],
    columnList: [],
    type: "",
    paginationObj: null,
    onPageChange: null,
    loading: false,
    isReadOnly: false,
};

export default ContractAlertTable;